import { CheckoutFormElemetDataType } from '@/components/sections/builder/elements/body/checkout-form/type';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, formatIDR, isEmpty } from '@/lib/utils';
import { HttpTypes } from '@/types';
import { BaseCart } from '@/types/http/cart/common';
import { CircleHelp, ShoppingBagIcon } from 'lucide-react';
import Image from 'next/image';
import React from 'react';

type CheckoutItemProps = {
  content: CheckoutFormElemetDataType;
  carts: HttpTypes.StoreCartDetail;
  isShippingInsurance: boolean;
  isLoadingSelectShipping: boolean;
  className?: string;
};

export const CheckoutItem: React.FC<CheckoutItemProps> = ({
  content,
  carts,
  isShippingInsurance,
  isLoadingSelectShipping,
  className,
}) => {
  const subtotal = carts?.subtotal as number;
  const discountAmount = carts?.discount_amount as number;
  const grandTotal = carts?.grand_total as number;
  const shippingCost = carts?.shipping_cost as number;
  const shippingInsurance = carts?.shipping_insurance as number;
  const CODFee = carts?.cod_fee as number;
  const CODInsurance = carts?.cod_fee_shipping_insurance as number;

  const totalCOD = CODFee + CODInsurance;
  const total = isShippingInsurance ? grandTotal + shippingInsurance : grandTotal;

  return (
    <>
      <div className={cn('bg_checkout_item flex flex-col p-6 md:w-[45%] md:p-9', className)}>
        <div className={cn(content.stickyContent && `top-sticky md:sticky`)}>
          <div className='flex items-center gap-x-2 pb-6'>
            <ShoppingBagIcon />
            <h2 className='text-xl'>Your Order Details</h2>
          </div>
          <h3 className='mb-4 text-base font-semibold'>Product</h3>
          {carts?.items?.map((item: BaseCart, index: number) => (
            <div
              className='mb-5 flex flex-col justify-between border-b pb-5 md:flex-row md:items-center md:gap-6'
              key={index}
            >
              <div className='flex w-full max-w-full items-center gap-x-4 overflow-hidden'>
                <div className='relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-sm'>
                  <Image
                    src={item.product_assets[0]?.assets_url}
                    alt={item.product_name}
                    fill
                    className='rounded-sm border border-gray-300 object-cover'
                  />
                  <span
                    style={
                      !isEmpty(content.colorSchemeForm)
                        ? {
                            backgroundColor: content.colorSchemeForm?.btnSolidBackgroud,
                            color: content?.colorSchemeForm?.btnSolidlabel,
                          }
                        : {}
                    }
                    className='absolute right-0 top-0 flex h-5 w-5 items-center justify-center rounded-bl-sm bg-black/60 text-xs font-bold text-white'
                  >
                    {item.quantity}
                  </span>
                </div>
                <div className='flex w-full max-w-full flex-col overflow-hidden'>
                  <p className='truncate break-all text-sm md:break-words'>{item.product_name}</p>
                  <span className='truncate break-all text-xs text-gray-500 md:break-words'>
                    {item.sku}
                  </span>
                </div>
              </div>
              <div className='flex flex-col gap-2'>
                {(item?.discount_price as number) > 0 && (
                  <span className='ml-20 whitespace-nowrap text-sm font-bold md:ml-0'>
                    {formatIDR((item?.discount_price as number) * item.quantity)}
                  </span>
                )}
                <span
                  className={cn(
                    'ml-20 whitespace-nowrap text-sm md:ml-0',
                    item?.discount_price ? 'text-[#C2C2C2] line-through' : 'font-bold'
                  )}
                >
                  {formatIDR(item?.sell_price * item.quantity)}
                </span>
              </div>
            </div>
          ))}
          <div className='mt-6 flex w-full items-center justify-between border-b pb-4 text-sm'>
            <span>Subtotal</span>
            <span>{formatIDR(subtotal)}</span>
          </div>
          {discountAmount > 0 && (
            <div className='mt-6 flex w-full items-center justify-between border-b pb-4 text-sm'>
              <span>Product discounts</span>
              <span className='text-red-500'>{formatIDR(discountAmount)}</span>
            </div>
          )}
          {isLoadingSelectShipping ? (
            <div className='mt-4 flex items-center justify-between border-b pb-4 text-sm'>
              <span className=''>Shipping</span>
              <span>Estimated...</span>
            </div>
          ) : (
            carts?.shipping && (
              <div className='mt-4 flex items-center justify-between border-b pb-4 text-sm'>
                <span className=''>Shipping</span>
                <span>{formatIDR(shippingCost)}</span>
              </div>
            )
          )}
          {isShippingInsurance && (
            <div className='mt-4 flex items-center justify-between border-b pb-4 text-sm'>
              <span className=''>Shipping Insurance</span>
              <span>{formatIDR(Number(shippingInsurance))}</span>
            </div>
          )}
          {carts?.is_cod && (
            <div className='mt-4 flex items-center justify-between border-b pb-4 text-sm'>
              <span className=''>COD fee</span>
              <span>{formatIDR(Number(totalCOD))}</span>
            </div>
          )}
          <div className='mt-4 flex items-center justify-between border-b pb-4 text-sm'>
            <span className=''>Platform fee</span>
            <span>{formatIDR(Number(carts?.platform_fee))}</span>
          </div>

          <div className='mt-4 flex items-center justify-between border-b pb-4 text-sm'>
            <div className='flex items-center gap-x-2'>
              <span>Estimated taxes</span>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger>
                    <CircleHelp className='h-4 w-4 text-gray-600' />
                  </TooltipTrigger>
                  <TooltipContent className='w-40 bg-black/80 text-center text-white'>
                    <p>
                      The final tax and total will be confirmed by email or text after you place
                      your order.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <span>{formatIDR(carts?.tax_amount as number)}</span>
          </div>
          <div className='checkout_item_content mt-6 flex items-center justify-between text-xl'>
            <span>Total</span>
            <span>{formatIDR(total)}</span>
          </div>
        </div>
      </div>
      <style jsx>
        {`
          .bg_checkout_item {
            background-color: ${content.colorSchemeItem?.background || 'white'};
            color: ${content.colorSchemeItem?.text || '#52535B'};
            border-radius: ${content.radiusCheckoutItem || 0}px;
          }
          @media (min-width: 768px) {
            .top-sticky {
              top: ${content.topContent || 0}px;
            }
          }
          .checkout_item_content {
            color: ${content.colorSchemeItem?.textSecondary || '#52535B'};
            font-weight: 700;
          }
        `}
      </style>
    </>
  );
};
